import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    QueryList,
    ViewChild,
    ViewChildren,
    ViewEncapsulation
} from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { NgbDate, NgbDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { TranslationWidth } from '@angular/common';
import type { SwiperOptions } from 'swiper/types/swiper-options';
import { FtSwiperDirective } from 'apps/account/src/account-shared/modules/swiper/ft-swiper.directive';
import { CalendarBase } from '../classes/calendar-base';
import { DateUtils } from '@flipto/shared/src/lib/utils/common/time/date-utils';
import {
    SelectableDateType
} from 'apps/account/src/account-shared/modules/monthly-daterange-picker/enums/selecable-date-type.enum';
import {
    AVAILABLE_DAY_RANGES,
    DATEPICKER_FIXED_RANGE_DAYS,
    DATEPICKER_MAX_DATE,
    DATEPICKER_MIN_DATE,
    DATEPICKER_TEXTS,
    MonthlyDateRangePickerTexts,
} from 'apps/account/src/account-shared';
import { SelectMode } from 'apps/account/src/account-shared/modules/monthly-daterange-picker/enums/select-mode.enum';
import dayjs from 'dayjs/esm/index';
import isBetween from 'dayjs/esm/plugin/isBetween';

dayjs.extend(isBetween);

@Component({
    selector: 'ft-monthly-daterange-picker',
    templateUrl: './monthly-daterange-picker.component.html',
    styleUrls: ['./monthly-daterange-picker.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('expandCollapse', [
            state('void', style({ height: '0', opacity: 0, overflow: 'hidden' })),
            state('*', style({ height: '*', opacity: 1, overflow: 'hidden' })),
            transition('void <=> *', [
                animate('500ms cubic-bezier(0.7, 0, 0.1, 1)')
            ])
        ])
    ]
})
export class MonthlyDaterangePickerComponent extends CalendarBase implements OnInit, AfterViewInit, OnDestroy {
    readonly selectModeEnum = SelectMode;
    @Output() changed = new EventEmitter<{ startDate: Date, endDate: Date }>();
    @Input() displayMonths = 24;
    @Input() fromDate: Date;
    @Input() toDate: Date;
    @Input() theme: 'dark' | 'light' | 'gray' = 'dark';
    @ViewChild('swiper', { static: false, read: FtSwiperDirective }) swiper?: FtSwiperDirective;
    @ViewChild('dp') datepicker: NgbDatepicker;
    @ViewChildren('calendarMonth') calendarMonths: QueryList<ElementRef>;
    initialCalendarDate: NgbDate;
    weekdaysWidth = TranslationWidth.Abbreviated;
    currentYear = new Date().getFullYear();
    initialSlideIndex: number;
    isNextButtonDisabled = true;
    isPrevButtonDisabled = true;
    preloadedMonths = 12;
    preloadedDatesRange: Date[];
    selectMode = this.availableDayRanges?.length ? SelectMode.Range : SelectMode.Day;

    timestamp = new Date().getTime();
    monthAnimation = true;
    hoveredMonth: Date = null;
    isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    allowClick = true;

    get swiperConfig(): SwiperOptions {
        return {
            roundLengths: true,
            simulateTouch: true,
            freeMode: { enabled: true, momentum: true, momentumRatio: 0.7, momentumBounce: false },
            slidesPerView: 'auto',
            initialSlide: this.initialSlideIndex,
            centeredSlides: true,
        };
    }

    constructor(@Optional() @Inject(DATEPICKER_MAX_DATE) public maxDate: Date,
                @Optional() @Inject(DATEPICKER_MIN_DATE) public minDate: Date,
                @Optional() @Inject(AVAILABLE_DAY_RANGES) public availableDayRanges: number[],
                @Optional() @Inject(DATEPICKER_TEXTS) public texts: MonthlyDateRangePickerTexts,
                @Optional() @Inject(DATEPICKER_FIXED_RANGE_DAYS) public fixedRangeDays: number | null,
                cdr: ChangeDetectorRef) {
        super(cdr);
        this.selectSingle = !!this.availableDayRanges?.length;

        // Set protected properties for base class availability checks
        this.maxDate = maxDate;
        this.minDate = minDate;
    }

    changeDetection() {
        this.cdr.detectChanges();
    }

    reRender() {
        this.timestamp = null;
        this.selectedMonths = [];

        this.changeDetection();
        this.timestamp = new Date().getTime();
        this.changeDetection();
    }

    ngOnInit(): void {
        this.initCalendar();
    }

    ngAfterViewInit() {
        this.focusCalendar();

        setTimeout(() => {
            this.setArrowsDisability();
        });

        // Disable month animation in order to not see animation each time when we toggle mode
        setTimeout(() => {
            this.monthAnimation = false;
        }, 700);
    }

    ngOnDestroy() {
    }

    onSlideChange(e: CustomEvent) {
        const swiperInstance = (e as any).detail?.[0];
        this.initialSlideIndex = swiperInstance?.activeIndex ?? this.initialSlideIndex;
        const centerMonth = DateUtils.add(DateUtils.ngbDateToDate(this.initialCalendarDate), this.initialSlideIndex - 1, 'month').toDate();
        this.preloadedDatesRange = [DateUtils.subtract(centerMonth, this.preloadedMonths, 'month').toDate(), DateUtils.add(centerMonth, this.preloadedMonths, 'month').toDate()];
    }

    onTouchStart() {
        this.allowClick = false;
    }

    onTouchEnd() {
        this.allowClick = true;
    }

    onSlideClick(e) {
        if (!this.allowClick) {
            e.preventDefault();
            e.stopPropagation();
        }
    }

    toggleMode() {
        this.selectMode = this.selectMode === SelectMode.Range ? SelectMode.Day : SelectMode.Range;
        this.reRender();
    }

    setMode(mode: SelectMode) {
        if (this.selectMode !== mode) {
            this.selectMode = mode;
            this.reRender();
        }
        // to be used in template logic
        return true;
    }


    isMonthSelected(date: Date | NgbDate) {
        const currentMonth = this.toDateFormat(date);

        return this.selectedMonths.some(month => dayjs(month).isSame(dayjs(currentMonth), 'month'))
    }

    isDateInView(date: NgbDate): boolean {
        if (this.preloadedDatesRange?.length) {
            return DateUtils.isBetween(this.preloadedDatesRange[0], this.preloadedDatesRange[1], DateUtils.ngbDateToDate(date));
        }
        return true;
    }

    initCalendar() {
        const totalMonthsFromSide = Math.round(this.displayMonths / 2);
        this.initialCalendarDate = DateUtils.dateToNgbDate(DateUtils.subtract(this.fromDate || new Date(), totalMonthsFromSide, 'month').toDate());
        this.focusDate = this.fromDate || new Date();
        this.initialSlideIndex = totalMonthsFromSide;

        if (this.fixedRangeDays) {
            this.markDisabled = (ngbDate: NgbDate) => {
                const date = DateUtils.ngbDateToDate(ngbDate);
                const rangeEnd = dayjs(date).add(this.fixedRangeDays - 1, 'day');
                if (this.maxDate && rangeEnd.isAfter(dayjs(this.maxDate), 'day')) {
                    return true;
                }
                if (this.minDate && dayjs(date).isBefore(dayjs(this.minDate), 'day')) {
                    return true;
                }
                return false;
            };
            return;
        }

        // Wrap to timeout in order to avoid stucked disabled state on days
        setTimeout(() => {
            if (this.fromDate) {
                this.startDate = this.fromDate;
            }
            if (this.toDate) {
                this.endDate = this.toDate;
            }
        });
    }

    isInactiveDate = (ngbDate: NgbDate): boolean => {
        if (!this.fixedRangeDays) return false;
        const date = DateUtils.ngbDateToDate(ngbDate);
        if (this.maxDate && dayjs(date).isAfter(dayjs(this.maxDate), 'day')) return false;
        if (this.minDate && dayjs(date).isBefore(dayjs(this.minDate), 'day')) return false;
        const rangeEnd = dayjs(date).add(this.fixedRangeDays - 1, 'day');
        return !!this.maxDate && rangeEnd.isAfter(dayjs(this.maxDate), 'day');
    };

    setArrowsDisability() {
        const activeIndex = this.swiper?.swiper?.activeIndex ?? 0;
        const slideCount = this.swiper?.swiper?.slides?.length ?? 0;
        if (window.matchMedia(`(min-width: 1800px)`).matches) {
            this.isPrevButtonDisabled = activeIndex <= 3;
            this.isNextButtonDisabled = activeIndex >= slideCount - 4;
        } else if (window.matchMedia(`(min-width: 1100px)`).matches) {
            this.isPrevButtonDisabled = activeIndex <= 2;
            this.isNextButtonDisabled = activeIndex >= slideCount - 3;
        } else {
            this.isPrevButtonDisabled = activeIndex <= 0;
            this.isNextButtonDisabled = activeIndex >= slideCount - 1;
        }
        this.cdr.detectChanges();
    }

    slideNext() {
        this.swiper.slideNext(300);
        this.setArrowsDisability();
    }

    slidePrev() {
        this.swiper.slidePrev(300);
        this.setArrowsDisability();
    }

    focusCalendar() {
        this.datepicker.focus();
        if (this.focusDate) {
            this.datepicker.focusDate(DateUtils.dateToNgbDate(this.focusDate));
        }
    }

    datepickerFocus() {
        this.datepicker.focus();
        this.changeDetection();
    }

    toggleMonthSelection(date: Date | NgbDate) {
        const startOfMonth = dayjs(this.toDateFormat(date)).startOf('month');
        const monthDate = startOfMonth.toDate();

        if (this.isMonthAvailable(monthDate)) {

            this.selectedMonths.push(monthDate);
            this.selectedMonths.sort((a, b) => a.getTime() - b.getTime());

            this.backfillMissingMonths();

            if (!!this.startDate) {
                this.setSelectedMonthsRange()
            }

            if (!this.startDate) {
                this.setStartDate(dayjs(this.selectedMonths[0]).startOf('month').toDate());
            }
        }
        this.selectedMonths = [...this.selectedMonths];

        this.cdr.detectChanges();
    }

    backfillMissingMonths() {
        if (this.selectedMonths.length < 2) {
            return;
        }

        const filledMonths: Date[] = [];

        for (let i = 0; i < this.selectedMonths.length; i++) {
            filledMonths.push(this.selectedMonths[i]);

            if (i < this.selectedMonths.length - 1) {
                const currentMonth = dayjs(this.selectedMonths[i]);
                const nextMonth = dayjs(this.selectedMonths[i + 1]);
                const monthsDiff = nextMonth.diff(currentMonth, 'month');

                if (monthsDiff > 1) {
                    let monthToAdd = currentMonth.add(1, 'month');
                    while (monthToAdd.isBefore(nextMonth, 'month')) {
                        if (this.isMonthAvailable(monthToAdd.toDate())) {
                            filledMonths.push(monthToAdd.toDate());
                        }
                        monthToAdd = monthToAdd.add(1, 'month');
                    }
                }
            }
        }

        this.selectedMonths = filledMonths.sort((a, b) => a.getTime() - b.getTime());
    }

    isMonthAvailable(date: Date | NgbDate): boolean {
        const month = this.toDateFormat(date);
        const monthStart = dayjs(month).startOf('month');
        const monthEnd = dayjs(month).endOf('month');
        const today = dayjs().startOf('day');

        if (this.minDate && monthEnd.isBefore(dayjs(this.minDate).startOf('day'), 'day')) {
            return false;
        }

        if (this.maxDate && monthStart.isAfter(dayjs(this.maxDate).endOf('day'), 'day')) {
            return false;
        }

        if (this.startDate) {
            return !monthStart.isBefore(dayjs(this.startDate).startOf('month')) && !monthStart.isAfter(today, 'day');
        }

        return !monthStart.isAfter(today, 'day');
    }

    isMonthInRange(date: Date | NgbDate): boolean {
        const month = this.toDateFormat(date);

        if (!this.startDate || !this.hoveredDate) {
            return false;
        }

        return dayjs(month).isBetween(dayjs(this.startDate), dayjs(this.hoveredDate), 'month', '[]');
    }

    isStartDateNextMonthAvailable(): boolean {
        if (!this.startDate)
            return false;
        return this.isMonthAvailable(dayjs(this.startDate).add(1, 'month').toDate());
    }

    setSelectedMonthsRange() {
        if (this.selectedMonths.length) {
            const firstMonth = this.selectedMonths[0];
            const lastMonth = this.selectedMonths[this.selectedMonths.length - 1];

            const firstAvailableDate = this.getFirstAvailableDateInMonth(firstMonth);
            const lastAvailableDate = this.getLastAvailableDateInMonth(lastMonth);

            if (firstAvailableDate) {
                this.setStartDate(firstAvailableDate);
            }
            if (lastAvailableDate) {
                this.setEndDate(lastAvailableDate);
            }
        }
    }

    private getFirstAvailableDateInMonth(month: Date): Date | null {
        const monthStart = dayjs(month).startOf('month');
        const monthEnd = dayjs(month).endOf('month');

        let firstAvailable = monthStart;

        if (this.minDate) {
            const minDateDayjs = dayjs(this.minDate).startOf('day');
            if (minDateDayjs.isAfter(monthEnd, 'day')) {
                return null;
            }
            if (minDateDayjs.isAfter(firstAvailable, 'day') && (minDateDayjs.isBefore(monthEnd, 'day') || minDateDayjs.isSame(monthEnd, 'day'))) {
                firstAvailable = minDateDayjs;
            }
        }


        if (firstAvailable.isAfter(monthEnd, 'day')) {
            return null;
        }

        return firstAvailable.toDate();
    }

    private getLastAvailableDateInMonth(month: Date): Date | null {
        const monthStart = dayjs(month).startOf('month');
        const monthEnd = dayjs(month).endOf('month');

        let lastAvailable = monthEnd;

        if (this.maxDate) {
            const maxDateDayjs = dayjs(this.maxDate).startOf('day');

            if (maxDateDayjs.isBefore(monthStart, 'day')) {
                return null;
            }

            if ((maxDateDayjs.isAfter(monthStart, 'day') || maxDateDayjs.isSame(monthStart, 'day')) &&
                (maxDateDayjs.isBefore(monthEnd, 'day') || maxDateDayjs.isSame(monthEnd, 'day'))) {
                lastAvailable = maxDateDayjs;
            }
        }

        if (lastAvailable.isBefore(monthStart, 'day')) {
            return null;
        }

        return lastAvailable.toDate();
    }

    onDateSelection(date: Date | NgbDate) {
        if (this.fixedRangeDays) {
            const d = this.toDateFormat(date);
            this.startDate = d;
            this.endDate = dayjs(d).add(this.fixedRangeDays - 1, 'day').toDate();
            this.onDatesSelected();
            return;
        }
        super.onDateSelection(date);
    }

    onDatesSelected() {
        this.changed.next({
            startDate: this.startDate,
            endDate: this.endDate
        });
    }

    onMonthHover(month: NgbDate | Date) {
        this.hoveredMonth = dayjs(this.toDateFormat(month)).endOf('month').toDate();
        this.changeDetection();
    }

    isRangeDisabled(date: Date) {
        return this.maxDate && DateUtils.isAfter(date, this.maxDate);
    }

    get selectableDateType() {
        if (this.startDate && this.endDate) {
            return SelectableDateType.Start;
        }
        return this.startDate && !this.endDate ? SelectableDateType.End : SelectableDateType.Start;
    }

    get selectedMonthsDaysTotalCount(): number {
        if (!this.selectedMonths || this.selectedMonths.length === 0) {
            return 0;
        }

        return this.selectedMonths.reduce((acc, month) => {
            const firstAvailableDate = this.getFirstAvailableDateInMonth(month);
            const lastAvailableDate = this.getLastAvailableDateInMonth(month);

            if (!firstAvailableDate || !lastAvailableDate) {
                return acc;
            }

            const firstDate = dayjs(firstAvailableDate).startOf('day');
            const lastDate = dayjs(lastAvailableDate).startOf('day');

            if (firstDate.isAfter(lastDate, 'day')) {
                return acc;
            }

            const daysInMonth = lastDate.diff(firstDate, 'day') + 1;
            return acc + daysInMonth;
        }, 0);
    }

    reset(event?: Event) {
        super.reset(event);

        this.hoveredMonth = null;
        this.toggleMode();
    }


}
